import {Component, computed, inject, input, InputSignal, output, OutputEmitterRef, ResourceRef, Signal, signal, WritableSignal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DxPopupModule} from 'devextreme-angular';
import {BonCfDTO} from '../../../../core/dtos/boncfs-dto';
import {BonReceptionDTO} from '../../../../core/dtos/bon-reception-dto';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {SharedModule} from '../../../../shared/shared.module';
import {MotifsNonConformiteService} from '../../../../core/services/entities/motifs-non-conformite.service';
import {BonReceptionService} from '../../../../core/services/entities/bon-reception.service';
import {GenericFormService} from '../../../../core/services/generics/generic-form.service';
import {UtilsService} from '../../../../core/utils/utils.service';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {MSG_KEY, MSG_SEVERITY, RECEPTION_STATUT, TYPES_CODES} from '../../../../core/constants';
import {UniqueCodeService} from '../../../../core/services/unique-code.service';
import {ReceptionStatutService} from '../../../../core/services/entities/reception-statut.service';
import {GenericDatagridService} from '../../../../core/services/generics/generic-datagrid.service';
import {ReceptionStatutDTO} from '../../../../core/dtos/reception-statut-dto';
import {ResponseWrapper} from "../../../../core/suppliers/wrappers/response-wrapper";

@Component({
  selector: 'yo-dialog-creation-bon-reception',
  templateUrl: './dialog-creation-bon-reception.component.html',
  styleUrls: ['./dialog-creation-bon-reception.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DxPopupModule,
    SharedModule
  ]
})
export class DialogCreationBonReceptionComponent {

  readonly ButtonThemeEnum = ButtonThemeEnum;

  protected readonly mncSvc: MotifsNonConformiteService = inject(MotifsNonConformiteService);

  #bonReceptionSvc: BonReceptionService = inject(BonReceptionService);
  #gfs: GenericFormService = inject(GenericFormService);
  #utils: UtilsService = inject(UtilsService);
  #toastSvc: ToastService = inject(ToastService);
  #uniqueCodeSvc: UniqueCodeService = inject(UniqueCodeService);
  #receptionStatutSvc: ReceptionStatutService = inject(ReceptionStatutService);
  #gds: GenericDatagridService = inject(GenericDatagridService);

  bonCf: InputSignal<BonCfDTO | undefined> = input<BonCfDTO | undefined>(undefined);
  selectedMotifIdList: WritableSignal<number[]> = signal<number[]>([]);

  onSaved: OutputEmitterRef<void> = output<void>();
  onClose: OutputEmitterRef<void> = output<void>();

  readonly #codeResource: ResourceRef<ResponseWrapper<string>> = this.#uniqueCodeSvc.generateUniqueCodeResource(() => ({ typeCode: TYPES_CODES.NUMERO_BON_RECEPTION }));
  readonly #statutResource: ResourceRef<ResponseWrapper<ReceptionStatutDTO>> = this.#gds.searchResource<ReceptionStatutDTO>(
    () => this.#receptionStatutSvc.filterStatut(RECEPTION_STATUT.NOUVEAU)
  );

  readonly #initData: Signal<{ bonReception: BonReceptionDTO, form: FormGroup<any> } | undefined> = computed(() => {
    if (!this.#codeResource.hasValue() || !this.#statutResource.hasValue())
      return undefined;
    const codeRes: ResponseWrapper<string> = this.#codeResource.value()!;
    const statutRes: ResponseWrapper<ReceptionStatutDTO> = this.#statutResource.value()!;
    if (this.#utils.isResponseSupplierError(codeRes) || this.#utils.isResponseSupplierError(statutRes))
      return undefined;
    const br: BonReceptionDTO = this.#bonReceptionSvc.initDTO(codeRes.one, this.bonCf()!, statutRes.resultList[0]);
    return {bonReception: br, form: this.#gfs.toFormGroup(this.#bonReceptionSvc.getFields(br)) as FormGroup};
  });

  readonly popupVisible: Signal<boolean> = computed(() => this.#initData() !== undefined);
  readonly form: Signal<FormGroup> = computed(() => this.#initData()?.form ?? new FormGroup({}));
  readonly bonReception: Signal<BonReceptionDTO | undefined> = computed(() => this.#initData()?.bonReception);


  close = (): void => {
    this.selectedMotifIdList.set([]);
    this.onClose.emit();
  };

  save = (): void => {
    this.#gfs.validateAllFormFields(this.form());
    if (this.form().valid) {
      this.#bonReceptionSvc.init(this.#buildBonReception()).subscribe(response => {
        if (!this.#utils.isResponseSupplierError(response)) {
          this.#onSaveSuccess(response.one);
        }
      });
    }
  };

  #buildBonReception(): BonReceptionDTO {
    const {dateLivraison, livreur, immatriculation, temperatureCamion, numeroBonLivraison} = this.form().value;
    return {
      ...this.bonReception()!,
      dateLivraison,
      livreur,
      immatriculation,
      temperatureCamion,
      numeroBonLivraison: numeroBonLivraison?.toUpperCase(),
    };
  }

  #onSaveSuccess(savedBr: BonReceptionDTO): void {
    savedBr.dateLivraison = this.#utils.convertNumberDateToDate(savedBr.dateLivraison);
    this.#toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Bon de réception ${savedBr.numeroBonReception} initialisé avec succès`);
    const idMotifNonConformiteGeneralList = this.selectedMotifIdList();
    this.close();
    this.#bonReceptionSvc.announceBonReceptionGridDxView(savedBr, idMotifNonConformiteGeneralList);
    this.onSaved.emit();
  }
}
